import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Search, Zap, Edit, Trash2, DollarSign, Eye, EyeOff, Music, Building2, Copy } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import CatalogoSubnav from "../../components/CatalogoSubnav";
import { ConfiguracionOperativaFields, configuracionOperativaEmpty } from "../../components/ConfiguracionOperativaFields";

const emptyForm = {
  nombre: "", descripcion: "", precio: 0,
  cobro_tipo: "fijo", costo_interno: 0,
  visible_publico: true, activo: true,
  es_dj_base: false, es_dj_hora_extra: false,
  es_salon_hora_extra: false, es_instalaciones: false,
  proveedor: "", notas_internas: "",
  categoria: "extra", orden: 0,
  configuracion_operativa: { ...configuracionOperativaEmpty },
};

const cobroTipos = [
  { value: "fijo", label: "Precio fijo" },
  { value: "por_persona", label: "Por persona" },
  { value: "por_nino", label: "Por nino" },
  { value: "por_hora", label: "Por hora" },
  { value: "por_unidad", label: "Por unidad" },
  { value: "por_evento", label: "Por evento" },
];

const categorias = ["extra", "dj", "musica", "decoracion", "instalaciones", "mobiliario", "personal", "actividad", "comida", "barra", "horas_extra", "otro"];

const ServiciosRapidos = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/servicios-rapidos`);
      setItems(res.data);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => { setEditing(null); setFormData(emptyForm); };

  const handleSave = async () => {
    if (!formData.nombre?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = {
        ...formData,
        precio: Number(formData.precio) || 0,
        costo_interno: Number(formData.costo_interno) || 0,
        orden: Number(formData.orden) || 0,
      };
      if (editing) {
        await axios.put(`${API}/servicios-rapidos/${editing.servicio_id}`, payload);
        toast.success("Servicio actualizado correctamente");
      } else {
        await axios.post(`${API}/servicios-rapidos`, payload);
        toast.success("Servicio creado");
      }
      setDialogOpen(false); resetForm(); await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar. Reintenta.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este servicio? Las cotizaciones y contratos anteriores NO seran modificados.")) return;
    try {
      await axios.delete(`${API}/servicios-rapidos/${id}`);
      toast.success("Eliminado");
      await fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleDuplicate = async (item) => {
    try {
      const { servicio_id: _id, created_at: _ca, usuario_id: _uid, ...rest } = item;
      void _id; void _ca; void _uid;
      await axios.post(`${API}/servicios-rapidos`, {
        ...rest,
        nombre: `${item.nombre} (copia)`,
        // No duplicar flags unicos (1 DJ base, 1 instalaciones, etc.)
        es_dj_base: false, es_instalaciones: false,
        es_dj_hora_extra: false, es_salon_hora_extra: false,
      });
      toast.success("Servicio duplicado");
      await fetchData();
    } catch { toast.error("Error al duplicar"); }
  };

  const toggleActivo = async (item) => {
    try {
      await axios.put(`${API}/servicios-rapidos/${item.servicio_id}`, { ...item, activo: !item.activo });
      toast.success(item.activo ? "Desactivado" : "Activado");
      await fetchData();
    } catch { toast.error("Error"); }
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({ ...emptyForm, ...item });
    setDialogOpen(true);
  };

  const filtered = items.filter(i =>
    i.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    i.categoria?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="servicios-rapidos-page">
      <CatalogoSubnav />
      <PageHeader
        title="Extras y servicios rapidos"
        description="Edita DJ, decoracion, mesa de dulces, horas extra y todos los servicios opcionales que aparecen en el cotizador publico y se pueden agregar a una cotizacion."
        primaryAction={{
          label: "Nuevo servicio",
          icon: Plus,
          testId: "btn-nuevo-servicio-rapido",
          onClick: () => { resetForm(); setDialogOpen(true); },
        }}
      />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input placeholder="Buscar por nombre o categoria..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 border-stone-300 h-11" data-testid="search-servicios-rapidos" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Zap className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-700">Sin servicios</h3>
          <p className="text-sm text-stone-500">Agrega DJ, horas extra, decoracion, mesa de dulces, etc.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(item => {
            const tipoLabel = cobroTipos.find(t => t.value === item.cobro_tipo)?.label || "Precio fijo";
            const isSpecial = item.es_dj_base || item.es_dj_hora_extra || item.es_salon_hora_extra || item.es_instalaciones;
            return (
              <Card key={item.servicio_id} className={`border-stone-200 ${!item.activo ? 'opacity-60' : ''} ${isSpecial ? 'ring-1 ring-amber-200' : ''}`} data-testid={`servicio-${item.servicio_id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {item.es_instalaciones && <Building2 className="w-4 h-4 text-blue-600" />}
                        {(item.es_dj_base || item.es_dj_hora_extra) && <Music className="w-4 h-4 text-purple-600" />}
                        {!isSpecial && <Zap className="w-4 h-4 text-amber-500" />}
                        <h3 className="font-medium text-stone-900 truncate">{item.nombre}</h3>
                      </div>
                      {item.descripcion && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{item.descripcion}</p>}
                      <div className="flex items-baseline gap-1 mt-2">
                        <DollarSign className="w-4 h-4 text-emerald-700" />
                        <span className="font-semibold text-stone-900">${Number(item.precio || 0).toLocaleString("es-MX")}</span>
                        <span className="text-xs text-stone-500">{tipoLabel}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2 flex-wrap text-[10px]">
                        <span className="bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded">{item.categoria}</span>
                        {item.es_dj_base && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">DJ base</span>}
                        {item.es_dj_hora_extra && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">DJ hora extra</span>}
                        {item.es_salon_hora_extra && <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Salon hora extra</span>}
                        {item.es_instalaciones && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Instalaciones</span>}
                        {!item.visible_publico && <span className="bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"><EyeOff className="w-3 h-3" /> Oculto</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 pt-2 border-t border-stone-100">
                    <Button variant="outline" size="sm" onClick={() => openEdit(item)} className="flex-1 h-9 text-xs" data-testid={`btn-edit-sr-${item.servicio_id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Editar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActivo(item)} title={item.activo ? "Desactivar" : "Activar"} className="h-9 px-2" data-testid={`btn-toggle-sr-${item.servicio_id}`}>
                      {item.activo ? <Eye className="w-4 h-4 text-stone-600" /> : <EyeOff className="w-4 h-4 text-stone-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(item)} title="Duplicar" className="h-9 px-2" data-testid={`btn-dup-sr-${item.servicio_id}`}>
                      <Copy className="w-4 h-4 text-stone-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.servicio_id)} className="h-9 px-2" data-testid={`btn-delete-sr-${item.servicio_id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { resetForm(); } setDialogOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-servicio-rapido">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar servicio rapido" : "Nuevo servicio rapido"}</DialogTitle>
            <DialogDescription className="text-xs">Los cambios afectan cotizaciones nuevas. Las anteriores (con copia congelada) NO se modifican.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Nombre *</Label>
                <Input value={formData.nombre} onChange={(e) => setFormData(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: DJ 5 horas / Hora extra DJ / Mesa de dulces" data-testid="input-sr-nombre" />
              </div>
              <div className="sm:col-span-2">
                <Label>Descripcion</Label>
                <Textarea rows={2} value={formData.descripcion || ""} onChange={(e) => setFormData(p => ({ ...p, descripcion: e.target.value }))} placeholder="Lo que el cliente vera en la cotizacion" data-testid="input-sr-descripcion" />
              </div>
              <div>
                <Label>Precio de venta (MXN) *</Label>
                <Input type="number" min="0" step="0.01" value={formData.precio} onChange={(e) => setFormData(p => ({ ...p, precio: e.target.value }))} data-testid="input-sr-precio" />
              </div>
              <div>
                <Label>Costo interno (privado)</Label>
                <Input type="number" min="0" step="0.01" value={formData.costo_interno || 0} onChange={(e) => setFormData(p => ({ ...p, costo_interno: e.target.value }))} placeholder="No visible al cliente" data-testid="input-sr-costo" />
              </div>
              <div>
                <Label>Tipo de cobro</Label>
                <Select value={formData.cobro_tipo} onValueChange={(v) => setFormData(p => ({ ...p, cobro_tipo: v }))}>
                  <SelectTrigger data-testid="select-sr-cobro"><SelectValue /></SelectTrigger>
                  <SelectContent>{cobroTipos.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData(p => ({ ...p, categoria: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categorias.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Proveedor (opcional)</Label>
                <Input value={formData.proveedor || ""} onChange={(e) => setFormData(p => ({ ...p, proveedor: e.target.value }))} placeholder="Nombre del proveedor" data-testid="input-sr-proveedor" />
              </div>
              <div>
                <Label>Orden de aparicion</Label>
                <Input type="number" value={formData.orden || 0} onChange={(e) => setFormData(p => ({ ...p, orden: e.target.value }))} />
              </div>
              <div className="sm:col-span-2">
                <Label>Notas internas (no se muestran al cliente)</Label>
                <Textarea rows={2} value={formData.notas_internas || ""} onChange={(e) => setFormData(p => ({ ...p, notas_internas: e.target.value }))} placeholder="Detalles privados, contacto del proveedor, montaje, etc." data-testid="input-sr-notas" />
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3 space-y-2">
              <p className="text-sm font-semibold text-stone-700">Visibilidad y estatus</p>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer">Activo (visible para uso interno)</Label>
                <Switch checked={formData.activo} onCheckedChange={(c) => setFormData(p => ({ ...p, activo: c }))} data-testid="switch-sr-activo" />
              </div>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer">Visible en cotizador publico (/cotizador)</Label>
                <Switch checked={formData.visible_publico} onCheckedChange={(c) => setFormData(p => ({ ...p, visible_publico: c }))} data-testid="switch-sr-publico" />
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3 space-y-2">
              <p className="text-sm font-semibold text-stone-700">Marcadores especiales (solo 1 por usuario)</p>
              <p className="text-xs text-stone-500 mb-2">Estas banderas conectan el servicio al cotizador automatico. NO confundir DJ base con hora extra DJ.</p>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer text-sm">DJ base (paquete completo de horas, ej: 5h)</Label>
                <Switch checked={formData.es_dj_base} onCheckedChange={(c) => setFormData(p => ({ ...p, es_dj_base: c, es_dj_hora_extra: c ? false : p.es_dj_hora_extra }))} data-testid="switch-sr-dj-base" />
              </div>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer text-sm">Hora extra DJ (precio por hora adicional)</Label>
                <Switch checked={formData.es_dj_hora_extra} onCheckedChange={(c) => setFormData(p => ({ ...p, es_dj_hora_extra: c, es_dj_base: c ? false : p.es_dj_base }))} data-testid="switch-sr-dj-hora" />
              </div>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer text-sm">Hora extra de salon / local</Label>
                <Switch checked={formData.es_salon_hora_extra} onCheckedChange={(c) => setFormData(p => ({ ...p, es_salon_hora_extra: c }))} data-testid="switch-sr-salon-hora" />
              </div>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer text-sm">Uso de instalaciones (cuando NO va incluido en paquete)</Label>
                <Switch checked={formData.es_instalaciones} onCheckedChange={(c) => setFormData(p => ({ ...p, es_instalaciones: c }))} data-testid="switch-sr-instalaciones" />
              </div>
            </div>

            <ConfiguracionOperativaFields
              value={formData.configuracion_operativa}
              onChange={(cfg) => setFormData(p => ({ ...p, configuracion_operativa: cfg }))}
              testidPrefix="config-op-sr"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="border-stone-300 h-11">Cancelar</Button>
            <Button onClick={handleSave} className="bg-emerald-900 hover:bg-emerald-800 h-11 px-6" data-testid="btn-guardar-sr">{editing ? "Guardar cambios" : "Crear servicio"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiciosRapidos;
